import { Directory, Filesystem } from '@capacitor/filesystem';
import { chain, get, replace } from 'lodash';
import pdfMake from 'pdfmake';
import pdfFonts from 'src/fonts/vfs_fonts';
import { normalizeDocDefinitionText } from 'src/utils/InvisibleCharactersUtils';
import { i18n } from 'src/boot/i18n';

/**
 * Should be called after successful {@link FunctionToHandle} code execution
 * @callback SuccessCallback
 */

/**
 * @callback FunctionToHandle
 * @param {SuccessCallback} cb - Should be called after successful code execution
 */

const DOWNLOAD_READY_TIMEOUT_TIME = 30000;
export const fonts = () => {
  return {
    PTSerif: {
      normal: 'PTSerif-Regular.ttf',
      bold: 'PTSerif-Bold.ttf',
      italics: 'PTSerif-Italic.ttf',
      bolditalics: 'PTSerif-BoldItalic.ttf',
    },
    Cairo: {
      normal: 'Cairo-Regular.ttf',
      bold: 'Cairo-Bold.ttf',
      italics: 'Cairo-Regular.ttf',
      bolditalics: 'Cairo-Bold.ttf',
    },
    Tajaval: {
      normal: 'Tajaval-Regular.ttf',
      bold: 'Tajaval-Bold.ttf',
      italics: 'Tajaval-Regular.ttf',
      bolditalics: 'Tajaval-Bold.ttf',
    },
  };
};

/**
 * @param {Object} doc
 * @return {Document}
 */
const createDocument = (doc) => {
  return pdfMake.createPdf(normalizeDocDefinitionText(doc), null, fonts(), pdfFonts.pdfMake.vfs);
};

// 5 matching CONCURRENCY in createAuditRun.js
const IMAGE_RESOLVE_CONCURRENCY = 5;
// retries/delay matching retryWrapper's defaults in functions/utils/helpers.js
const IMAGE_RESOLVE_RETRIES = 3;
const IMAGE_RESOLVE_RETRY_DELAY = 1000;
// matching the timeout used for the equivalent fetch on the backend, functions/utils/pdfCreationUtils.js
const IMAGE_FETCH_TIMEOUT = 10000;

const isRemoteImageUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// 429/5xx are treated as transient and retried; other statuses (403/404, ...) won't succeed on retry
const isRetryableStatus = (status) => status === 429 || status >= 500;

/**
 * Fetches a single image and converts it to a data URL, retrying transient failures with a fixed
 * delay and aborting any single attempt that stalls past IMAGE_FETCH_TIMEOUT.
 *
 * @param {string} url
 * @return {Promise<string>}
 */
const fetchImageAsDataUrl = async (url) => {
  let lastError;

  for (let attempt = 0; attempt < IMAGE_RESOLVE_RETRIES; attempt += 1) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), IMAGE_FETCH_TIMEOUT);
    let isTerminal = false;

    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(url, { signal: controller.signal });

      if (response.ok) {
        const contentType = response.headers.get('content-type') || 'image/png';
        // eslint-disable-next-line no-use-before-define, no-await-in-loop
        const base64 = await arrayBufferToBase64(await response.arrayBuffer());
        return `data:${contentType};base64,${base64}`;
      }

      lastError = new Error(`Image request failed with status ${response.status}`);
      lastError.status = response.status;
      if (!isRetryableStatus(response.status)) {
        isTerminal = true;
        throw lastError;
      }
      controller.abort();
    } catch (err) {
      lastError = err;
      if (isTerminal) {
        throw err;
      }
    } finally {
      clearTimeout(timeoutId);
    }

    if (attempt < IMAGE_RESOLVE_RETRIES - 1) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(IMAGE_RESOLVE_RETRY_DELAY);
    }
  }

  throw lastError;
};

/**
 * Replaces every remote image URL in doc.images with a locally-resolved data URL in place,
 * downloading at most IMAGE_RESOLVE_CONCURRENCY at a time.
 *
 * @param {Object} doc
 * @return {Promise<void>}
 */
const resolveDocImages = async (doc) => {
  const keys = Object.keys(doc.images || {}).filter((key) => isRemoteImageUrl(doc.images[key]));
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < keys.length) {
      const key = keys[nextIndex];
      nextIndex += 1;
      try {
        // eslint-disable-next-line no-await-in-loop
        doc.images[key] = await fetchImageAsDataUrl(doc.images[key]);
      } catch (err) {
        nextIndex = keys.length;
        throw err;
      }
    }
  }

  const workers = Array.from(
    { length: Math.min(IMAGE_RESOLVE_CONCURRENCY, keys.length) },
    () => worker(),
  );

  try {
    await Promise.all(workers);
  } catch (err) {
    const error = new Error(i18n.t('common.errors.export'), { cause: err });
    error.status = err.status;
    throw error;
  }
};

/**
 * @param {FunctionToHandle} insecureFunc - Function that can potentially throw unhandled errors
 * @param {number?} errorTimeout - Time in ms for 'timeout' error to be thrown
 * if no {@link SuccessCallback} had been called.
 */
export function handleRejections(insecureFunc, errorTimeout = 60000) {
  return new Promise((res, rej) => {
    const timeoutId = setTimeout(() => {
      // eslint-disable-next-line no-use-before-define
      window.removeEventListener('unhandledrejection', onRejection);
      rej(new Error('Timeout error'));
    }, errorTimeout);

    function onRejection(rejectionEvent) {
      clearTimeout(timeoutId);
      window.removeEventListener('unhandledrejection', onRejection);
      rej(new Error(get(rejectionEvent, ['reason', 'message'], get(rejectionEvent, 'reason'))));
    }

    window.addEventListener('unhandledrejection', onRejection);
    insecureFunc((exportedDocument) => {
      clearTimeout(timeoutId);
      window.removeEventListener('unhandledrejection', onRejection);
      res(exportedDocument);
    });
  });
}

/**
 * Download pdf document
 *
 * @param {Object} doc
 * @param {string} fileName
 */
export async function exportPdfFile(doc, fileName) {
  await resolveDocImages(doc);
  return handleRejections(
    (cb) => createDocument(doc).download(fileName, cb),
    DOWNLOAD_READY_TIMEOUT_TIME,
  );
}

/**
 * Get the pdf document in base64 string
 *
 * @param {Object} doc
 * @return {Promise<string>}
 */
export async function getPdfBase64(doc) {
  await resolveDocImages(doc);
  return handleRejections(
    (cb) => createDocument(doc).getBase64(cb),
    DOWNLOAD_READY_TIMEOUT_TIME,
  );
}

/**
 * Get the pdf document as blob
 *
 * @param {Object} doc
 * @return {Promise<Blob>}
 */
export async function getPdfBlob(doc) {
  await resolveDocImages(doc);
  return handleRejections(
    (cb) => createDocument(doc).getBlob(cb),
    DOWNLOAD_READY_TIMEOUT_TIME,
  );
}

/**
 * Searches through Documents folder on user's device and creates a unique file name
 * if there already exists a file with the provided name
 *
 * @param {string} fileName
 * @return {Promise<string>}
 */
export async function findFreeFileNameOnNativeApp(fileName) {
  const format = chain(fileName).split('.').last().value();
  const nameWithoutFormat = replace(fileName, `.${format}`, '');
  let nameSuffix = 1;
  let attempt = fileName;
  let accessibleFileName;

  while (!accessibleFileName) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await Filesystem.stat({
        path: attempt,
        directory: Directory.Documents,
      });
    } catch (err) {
      // Found name that is not exists
      accessibleFileName = attempt;
      break;
    }

    attempt = `${nameWithoutFormat} (${nameSuffix}).${format}`;
    nameSuffix += 1;
  }

  return accessibleFileName;
}

/**
 * Converts an ArrayBuffer to a Base64 string
 * @param {ArrayBuffer} buffer - The ArrayBuffer to convert
 * @returns {Promise<string>}
 */
export async function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const binary = bytes.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
  return btoa(binary);
}
